import React from 'react';
import styled from 'styled-components';
import { Scrim, Modal, Btn, Tag, StatusBadge } from '../ui/primitives';

const TIER_TONE = {
  starter: 'gray', professional: 'blue', growth: 'accent',
  organization: 'green', enterprise: 'amber',
};

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

/** epoch seconds → readable date, or an em dash. */
function fmtDate(epoch) {
  if (epoch === null || epoch === undefined || epoch === '') return '—';
  const d = new Date(Number(epoch) * 1000);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** grant_duration_days → human string, or the standard-term fallback. */
function grantLabel(code) {
  const d = code?.grantDurationDays;
  if (d !== null && d !== undefined && d !== '' && Number(d) > 0) {
    const n = Number(d);
    return n % 30 === 0 ? `${n / 30} month${n / 30 === 1 ? '' : 's'} from activation` : `${n} days from activation`;
  }
  return code?.applicability === 'yearly'
    ? 'Standard — 365 days (yearly)'
    : 'Standard — 30 days (monthly)';
}

function billingLabel(a) {
  if (a === 'yearly') return 'Yearly';
  if (a === 'monthly') return 'Monthly';
  return 'Monthly or yearly';
}

/**
 * Read-only details view for one activation code. Org-scoped codes show the
 * organization name (resolved by the parent and passed as `orgName`).
 */
export default function ActivationCodeDetailsDialog({ open, code, orgName, onClose, onEdit }) {
  if (!open || !code) return null;

  const isOrg = !!code.orgId;
  const used = code.redemptionCount ?? 0;
  const usesLabel = code.maxTotalUses ? `${used} / ${code.maxTotalUses}` : `${used} / ∞`;

  return (
    <Scrim onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <Modal style={{ maxWidth: 480 }}>
        <div className="modal__head">
          <div>
            <h3>{code.code}</h3>
            <p>Activation code details</p>
          </div>
          <button className="x-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal__body">
          <StatusRow>
            <StatusBadge $active={code.active}>{code.active ? 'Active' : 'Inactive'}</StatusBadge>
            {isOrg
              ? <Tag $tone="accent">Organization · one-time</Tag>
              : <Tag $tone="gray">Global · anyone can redeem</Tag>}
          </StatusRow>

          <Dl>
            {isOrg && (
              <Row>
                <dt>Organization</dt>
                <dd>{orgName || code.orgName || code.orgId}</dd>
              </Row>
            )}
            <Row>
              <dt>Plan</dt>
              <dd><Tag $tone={TIER_TONE[code.planId] || 'gray'}>{cap(code.planId) || '—'}</Tag></dd>
            </Row>
            <Row>
              <dt>Billing cycle</dt>
              <dd>{billingLabel(code.applicability)}</dd>
            </Row>
            <Row>
              <dt>Plan duration</dt>
              <dd>{grantLabel(code)}</dd>
            </Row>
            <Row>
              <dt>Code valid</dt>
              <dd>{fmtDate(code.validFrom)} – {fmtDate(code.validUntil)}</dd>
            </Row>
            <Row>
              <dt>Redemptions</dt>
              <dd>{usesLabel}</dd>
            </Row>
            <Row>
              <dt>Created</dt>
              <dd>{fmtDate(code.createdAt)}</dd>
            </Row>
          </Dl>
        </div>

        <div className="modal__foot">
          <Btn $variant="ghost" onClick={onClose}>Close</Btn>
          {onEdit && <Btn $variant="subtle" onClick={onEdit}>Edit</Btn>}
        </div>
      </Modal>
    </Scrim>
  );
}

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
`;

const Dl = styled.dl`
  margin: 0;
  display: grid;
  gap: 0;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 12px;
  align-items: center;
  padding: 11px 0;
  border-top: 1px solid ${({ theme }) => theme.border};
  &:last-child { border-bottom: 1px solid ${({ theme }) => theme.border}; }

  dt {
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
    font-weight: 600;
  }
  dd {
    margin: 0;
    font-size: 13.5px;
    color: ${({ theme }) => theme.text};
    font-weight: 600;
    word-break: break-word;
  }
`;
